import React, { useEffect, useState, useCallback } from "react";
import { useAdminEvent } from "@/context/AppContext";
import { api, assetUrl } from "@/lib/api";
import { PageIntro } from "@/admin/ui";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Plus, MoreVertical, Pencil, Send, Archive, Trash2, ArchiveRestore, EyeOff,
  ArrowUp, ArrowDown, UtensilsCrossed, Waves, Compass, BedDouble, ImagePlus, X, Upload,
  RefreshCw, Power, Undo2,
} from "lucide-react";

const CATS = [
  { key: "dining", label: "Dining & Bars", icon: UtensilsCrossed },
  { key: "pools", label: "Pools & Beach", icon: Waves },
  { key: "activities", label: "Activities & Excursions", icon: Compass },
  { key: "hotel", label: "Hotel Essentials", icon: BedDouble },
];
const DOW = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"],
  ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const STATUS_STYLE = {
  published: "bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))]",
  draft: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
  archived: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] line-through",
};

export default function ExploreAdmin() {
  const { eventId } = useAdminEvent();
  const [cat, setCat] = useState("dining");
  const [all, setAll] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.mgrExplore(eventId).then((r) => { setAll(r.items || []); setCats(r.categories || []); }).catch(() => {});
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const items = all.filter((i) => i.category === cat).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    await act(() => api.mgrExploreReorder(eventId, arr.map((x) => x.id)), "Reordered");
  };

  const newItem = () => {
    if (cat === "dining") return { category: "dining", type: "dining_venue", subtype: "restaurant", name: "", status: "draft", restrictions: [] };
    if (cat === "activities") return { category: "activities", type: "excursion", name: "", status: "draft" };
    if (cat === "pools") return { category: "pools", type: "pool_beach", name: "", status: "draft", facility_hours: [], facilities: [], rules: [] };
    return { category: "hotel", type: "hotel_essentials", name: "", status: "draft", sections: [] };
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <PageIntro>Maintain the attendee Explore guide for Hyatt Ziva Los Cabos. Only <strong>published</strong> items appear in the attendee app. Edits are event-scoped and save live.</PageIntro>
        <Button data-testid="explore-add-btn" onClick={() => setEditing(newItem())}><Plus className="h-4 w-4 mr-1" /> Add item</Button>
      </div>

      <ReleasePanel eventId={eventId} reload={load} />

      <CategoryImages eventId={eventId} cats={cats} reload={load} />

      <Tabs value={cat} onValueChange={setCat} className="mb-4">
        <TabsList>
          {CATS.map((c) => {
            const Icon = c.icon;
            const n = all.filter((i) => i.category === c.key && i.status === "published").length;
            return <TabsTrigger key={c.key} value={c.key} data-testid={`explore-tab-${c.key}`}><Icon className="h-4 w-4 mr-1.5" />{c.label}<span className="ml-1.5 text-[10px] opacity-60">{n}</span></TabsTrigger>;
          })}
        </TabsList>
      </Tabs>

      <div className="space-y-2">
        {items.length === 0 && <div className="premium-card p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No items in this category yet.</div>}
        {items.map((it, idx) => (
          <div key={it.id} className="premium-card p-4" data-testid={`explore-row-${it.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex flex-col items-center gap-0.5 pt-0.5">
                  <button className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] disabled:opacity-30" disabled={idx === 0} onClick={() => move(idx, -1)}><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] disabled:opacity-30" disabled={idx === items.length - 1} onClick={() => move(idx, 1)}><ArrowDown className="h-3.5 w-3.5" /></button>
                </div>
                {it.image_url
                  ? <img src={assetUrl(it.image_url)} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />
                  : <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))] shrink-0"><UtensilsCrossed className="h-5 w-5" /></div>}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-wide font-bold rounded-full px-2 py-0.5 ${STATUS_STYLE[it.status] || STATUS_STYLE.draft}`}>{it.status || "draft"}</span>
                    {it.subtype && <span className="text-[10px] uppercase font-semibold text-[hsl(var(--pc-gold))]">{it.subtype}</span>}
                    {it.cuisine && <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{it.cuisine}</span>}
                  </div>
                  <div className="font-semibold text-[hsl(var(--primary))] mt-0.5 truncate">{it.name}</div>
                  {it.location && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 truncate">{it.location}</div>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="outline" data-testid={`explore-edit-${it.id}`} onClick={() => setEditing(it)}><Pencil className="h-3.5 w-3.5" /></Button>
                {it.status !== "published"
                  ? <Button size="sm" data-testid={`explore-publish-${it.id}`} onClick={() => act(() => api.mgrExplorePublish(eventId, it.id, true), "Published")}><Send className="h-3.5 w-3.5 mr-1" /> Publish</Button>
                  : <Button size="sm" variant="outline" data-testid={`explore-unpublish-${it.id}`} onClick={() => act(() => api.mgrExplorePublish(eventId, it.id, false), "Unpublished")}><EyeOff className="h-3.5 w-3.5 mr-1" /> Unpublish</Button>}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button size="sm" variant="outline"><MoreVertical className="h-3.5 w-3.5" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {it.status !== "archived"
                      ? <DropdownMenuItem onClick={() => act(() => api.mgrExploreArchive(eventId, it.id, true), "Archived")}><Archive className="h-4 w-4 mr-2" /> Archive</DropdownMenuItem>
                      : <DropdownMenuItem onClick={() => act(() => api.mgrExploreArchive(eventId, it.id, false), "Restored")}><ArchiveRestore className="h-4 w-4 mr-2" /> Restore</DropdownMenuItem>}
                    {(it.status || "draft") === "draft" && <DropdownMenuItem className="text-red-600" onClick={() => act(() => api.mgrExploreDelete(eventId, it.id), "Deleted")}><Trash2 className="h-4 w-4 mr-2" /> Delete draft</DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && <Editor eventId={eventId} item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

const FOCALS = ["center", "top", "bottom", "left", "right"];

function ReleasePanel({ eventId, reload }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [backups, setBackups] = useState([]);
  const [confirmText, setConfirmText] = useState("");
  const [selBackup, setSelBackup] = useState("");

  const refresh = useCallback(() => {
    api.mgrExploreStatus(eventId).then(setStatus).catch(() => {});
  }, [eventId]);
  useEffect(() => { refresh(); }, [refresh]);

  // Backend may return a string detail OR a structured {message, readiness} object.
  const errMsg = (e) => {
    const d = e?.response?.data?.detail;
    if (!d) return "Action failed";
    if (typeof d === "string") return d;
    return d.message || "Action failed";
  };

  const run = async (key, fn, msg) => {
    setBusy(key);
    try { const r = await fn(); if (r) setResult(r); toast.success(msg); refresh(); reload && reload(); }
    catch (e) { toast.error(errMsg(e)); if (e?.response?.data?.detail?.readiness) setResult(e.response.data.detail); }
    finally { setBusy(""); }
  };

  const openRestore = async () => {
    try { const r = await api.mgrExploreBackups(eventId); setBackups(r.backups || []); setSelBackup((r.backups || [])[0]?.id || ""); setConfirmText(""); setRestoreOpen(true); }
    catch (e) { toast.error("Could not load backups"); }
  };

  const doRestore = async () => {
    if (confirmText !== "RESTORE" || !selBackup) return;
    setBusy("restore");
    try { const r = await api.mgrExploreRestore(eventId, selBackup); setResult(r); toast.success("Restore complete"); setRestoreOpen(false); refresh(); reload && reload(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  const activated = status?.activated;
  const readiness = status?.readiness;
  const canActivate = !readiness || readiness.ready;
  return (
    <div className="premium-card p-4 mb-4" data-testid="explore-release-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-[hsl(var(--primary))]">Release controls <span className="text-[10px] uppercase font-bold text-[hsl(var(--muted-foreground))]">platform admin</span></div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
            Status:{" "}
            <span className={`font-semibold ${activated ? "text-[hsl(var(--secondary))]" : "text-[hsl(var(--pc-gold))]"}`} data-testid="release-activation-state">
              {activated ? "Active (visible to attendees)" : "Not activated (attendees see holding screen)"}
            </span>
            {status && <> · Published {status.content_by_status?.published || 0} · Images {status.categories_with_image}/{status.categories} · Backups {status.backup_count}</>}
          </div>
          {status?.latest_backup && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">Latest backup: <code>{status.latest_backup.id}</code></div>}
          {readiness && !activated && !readiness.ready && (
            <div className="text-[11px] mt-1 text-[hsl(var(--pc-gold))]" data-testid="release-readiness-blockers">
              Activation blocked:{" "}
              {!readiness.backup_ok && <span>no verified backup · </span>}
              {!readiness.content_ok && <span>no published content · </span>}
              {!readiness.images_ok && <span>category images {readiness.categories_image_linked}/{readiness.categories_total} ready{readiness.categories_image_unretrievable?.length ? " (some unretrievable)" : ""}</span>}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button size="sm" disabled={busy === "migrate"} onClick={() => run("migrate", () => api.mgrExploreMigrate(eventId), "Migration complete")} data-testid="release-migrate"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Run migration</Button>
        <Button size="sm" variant="outline" disabled={busy === "dry"} onClick={() => run("dry", () => api.mgrExploreRollbackDryRun(eventId), "Dry-run complete")} data-testid="release-dryrun"><Undo2 className="h-3.5 w-3.5 mr-1" /> Rollback dry-run</Button>
        {activated
          ? <Button size="sm" variant="outline" onClick={() => run("act", () => api.mgrExploreActivate(eventId, false), "Deactivated")} data-testid="release-deactivate"><Power className="h-3.5 w-3.5 mr-1" /> Deactivate</Button>
          : <Button size="sm" disabled={!canActivate || busy === "act"} title={!canActivate ? "Resolve activation blockers first" : undefined} onClick={() => run("act", () => api.mgrExploreActivate(eventId, true), "Activated")} data-testid="release-activate"><Power className="h-3.5 w-3.5 mr-1" /> Activate Explore</Button>}
        <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={openRestore} data-testid="release-restore-open"><Trash2 className="h-3.5 w-3.5 mr-1" /> Restore from backup…</Button>
      </div>

      {result && (
        <pre className="mt-3 text-[11px] bg-[hsl(var(--muted))] rounded-lg p-2.5 overflow-x-auto" data-testid="release-result">{JSON.stringify(result, null, 2)}</pre>
      )}

      <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <DialogContent data-testid="release-restore-dialog">
          <DialogHeader><DialogTitle>Restore from backup (destructive)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Restores this event's content & categories from the selected pre-migration backup. Manager edits made since are preserved and reported as conflicts; shared image files are not deleted.</p>
            <div>
              <Label>Backup</Label>
              <select value={selBackup} onChange={(e) => setSelBackup(e.target.value)} className="w-full rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="release-backup-select">
                {backups.map((b) => <option key={b.id} value={b.id}>{b.id} · {b.created_at}</option>)}
              </select>
            </div>
            <div>
              <Label>Type RESTORE to confirm</Label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="RESTORE" data-testid="release-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreOpen(false)}>Cancel</Button>
            <Button className="text-red-600" disabled={confirmText !== "RESTORE" || !selBackup || busy === "restore"} onClick={doRestore} data-testid="release-restore-confirm">Restore</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CategoryImages({ eventId, cats, reload }) {
  const [busy, setBusy] = useState(null);
  const ordered = [...(cats || [])].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  const upload = async (catId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(catId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.mgrExploreUploadImage(eventId, fd);
      await api.mgrExploreCategory(eventId, catId, { image_asset_id: r.asset_id });
      toast.success("Image updated");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(null); e.target.value = ""; }
  };
  const remove = async (c) => {
    setBusy(c.id);
    try {
      if (c.image_asset_id) await api.mgrExploreRemoveImage(eventId, c.image_asset_id);
      else await api.mgrExploreCategory(eventId, c.id, { image_asset_id: null });
      toast.success("Image removed");
      reload();
    } catch (err) { toast.error("Could not remove"); }
    finally { setBusy(null); }
  };
  const setFocal = async (c, v) => {
    try { await api.mgrExploreCategory(eventId, c.id, { image_focal: v }); reload(); }
    catch (e) { toast.error("Could not update"); }
  };
  if (!ordered.length) return null;
  return (
    <div className="premium-card p-4 mb-4" data-testid="explore-category-images">
      <div className="font-semibold text-[hsl(var(--primary))] mb-1">Explore landing images</div>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">These appear on the attendee Explore home cards. Landscape images look best; cropping uses the focal position.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {ordered.map((c) => (
          <div key={c.id} className="flex gap-3 rounded-lg border border-[hsl(var(--border))] p-2.5" data-testid={`category-image-${c.key}`}>
            {c.image_url
              ? <img src={assetUrl(c.image_url)} alt="" className="h-16 w-24 rounded-md object-cover shrink-0" style={{ objectPosition: c.image_focal || "center" }} />
              : <div className="h-16 w-24 rounded-md bg-[hsl(var(--muted))] grid place-items-center text-[hsl(var(--muted-foreground))] shrink-0"><ImagePlus className="h-5 w-5" /></div>}
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm text-[hsl(var(--primary))] truncate">{c.label}</div>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                <label className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--border))] px-2 py-1 text-xs font-medium cursor-pointer hover:bg-[hsl(var(--muted))]">
                  <Upload className="h-3.5 w-3.5" /> {busy === c.id ? "…" : (c.image_url ? "Replace" : "Upload")}
                  <input type="file" accept="image/*" className="hidden" disabled={busy === c.id} onChange={(e) => upload(c.id, e)} data-testid={`category-upload-${c.key}`} />
                </label>
                {c.image_url && <Button type="button" size="sm" variant="outline" onClick={() => remove(c)} data-testid={`category-remove-${c.key}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                {c.image_url && (
                  <select value={c.image_focal || "center"} onChange={(e) => setFocal(c, e.target.value)} className="rounded-md border border-[hsl(var(--border))] px-2 py-1 text-xs" data-testid={`category-focal-${c.key}`}>
                    {FOCALS.map((ff) => <option key={ff} value={ff}>{ff}</option>)}
                  </select>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Editor({ eventId, item, onClose, onSaved }) {
  const isNew = !item.id;
  const [f, setF] = useState({ restrictions: [], facility_hours: [], facilities: [], rules: [], sections: [], ...item });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isDining = f.type === "dining_venue" || f.type === "pool_menu_collection";
  const isExc = f.type === "excursion";
  const isPool = f.type === "pool_beach";
  const isHotel = f.type === "hotel_essentials";
  const isDmc = f.type === "dmc_info";

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.mgrExploreUploadImage(eventId, fd);
      set("image_asset_id", r.asset_id);
      set("image_url", r.url);
      toast.success("Image uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const removeImg = async () => {
    if (f.image_asset_id) { try { await api.mgrExploreRemoveImage(eventId, f.image_asset_id); } catch (e) {} }
    set("image_asset_id", null); set("image_url", null);
  };

  const save = async () => {
    if (!(f.name || "").trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const body = {
        name: f.name, subtitle: f.subtitle, description: f.description, cuisine: f.cuisine,
        location: f.location, dress_code: f.dress_code, extension: f.extension, notes: f.notes,
        reservation_note: f.reservation_note, booking_note: f.booking_note, season_note: f.season_note,
        external_url: f.external_url, overview: f.overview, checkout: f.checkout, amstar_desk: f.amstar_desk,
        role: f.role, ocean_notice: f.ocean_notice, beach_note: f.beach_note, internal_notes: f.internal_notes,
        restrictions: f.restrictions, facility_hours: f.facility_hours, facilities: f.facilities,
        rules: f.rules, sections: f.sections, schedule: f.schedule, image_asset_id: f.image_asset_id,
      };
      if (isNew) {
        body.type = f.type; body.category = f.category; body.subtype = f.subtype;
        await api.mgrExploreCreate(eventId, body);
      } else {
        if (f.subtype) body.subtype = f.subtype;
        await api.mgrExploreUpdate(eventId, item.id, body);
      }
      toast.success(isNew ? "Item created" : "Item saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="explore-editor">
        <DialogHeader><DialogTitle>{isNew ? "Add item" : "Edit item"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {isDining && isNew && (
            <div><Label>Type</Label>
              <select data-testid="explore-subtype" value={f.subtype || "restaurant"} onChange={(e) => set("subtype", e.target.value)} className="w-full rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm">
                <option value="restaurant">Restaurant</option>
                <option value="bar">Bar / Lounge</option>
              </select>
            </div>
          )}
          <div><Label>Name</Label><Input data-testid="explore-name" value={f.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
          {(isPool || isHotel) && <div><Label>Subtitle</Label><Input value={f.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} /></div>}
          {isDining && <div><Label>Cuisine / type</Label><Input value={f.cuisine || ""} onChange={(e) => set("cuisine", e.target.value)} /></div>}
          {isDmc && <div><Label>Role</Label><Input value={f.role || ""} onChange={(e) => set("role", e.target.value)} /></div>}

          <div><Label>{isPool ? "Overview (optional)" : "Description"}</Label><Textarea rows={3} value={f.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
          {isHotel && <div><Label>Overview</Label><Textarea rows={3} value={f.overview || ""} onChange={(e) => set("overview", e.target.value)} /></div>}
          {isHotel && <div className="grid grid-cols-2 gap-2"><div><Label>Checkout</Label><Input value={f.checkout || ""} onChange={(e) => set("checkout", e.target.value)} /></div></div>}
          {isHotel && <div><Label>Amstar desk</Label><Textarea rows={2} value={f.amstar_desk || ""} onChange={(e) => set("amstar_desk", e.target.value)} /></div>}

          {(isDining) && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Location</Label><Input value={f.location || ""} onChange={(e) => set("location", e.target.value)} /></div>
              <div><Label>Extension</Label><Input value={f.extension ?? ""} onChange={(e) => set("extension", e.target.value)} placeholder="leave blank to hide" /></div>
              <div className="col-span-2"><Label>Dress code</Label><Textarea rows={2} value={f.dress_code || ""} onChange={(e) => set("dress_code", e.target.value)} /></div>
              <div className="col-span-2"><Label>Reservations (optional)</Label><Input value={f.reservation_note || ""} onChange={(e) => set("reservation_note", e.target.value)} /></div>
              <div className="col-span-2"><Label>Good to know (optional)</Label><Input value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
            </div>
          )}
          {(isDmc) && <>
            <div><Label>Location</Label><Input value={f.location || ""} onChange={(e) => set("location", e.target.value)} /></div>
            <div><Label>Booking note</Label><Textarea rows={2} value={f.booking_note || ""} onChange={(e) => set("booking_note", e.target.value)} /></div>
          </>}
          {isExc && <>
            <div><Label>Season note (optional)</Label><Textarea rows={2} value={f.season_note || ""} onChange={(e) => set("season_note", e.target.value)} /></div>
            <div><Label>Booking note</Label><Textarea rows={2} value={f.booking_note || ""} onChange={(e) => set("booking_note", e.target.value)} /></div>
          </>}

          {isPool && <>
            <div><Label>Ocean-entry notice</Label><Textarea rows={3} value={f.ocean_notice || ""} onChange={(e) => set("ocean_notice", e.target.value)} /></div>
            <div><Label>Beach note</Label><Textarea rows={2} value={f.beach_note || ""} onChange={(e) => set("beach_note", e.target.value)} /></div>
            <PairListEditor label="Facility hours" value={f.facility_hours} aKey="label" bKey="detail" aPh="Pools" bPh="Daily 08:00–20:00" onChange={(v) => set("facility_hours", v)} />
            <LinesEditor label="Facilities" value={f.facilities} onChange={(v) => set("facilities", v)} />
            <LinesEditor label="Pool rules" value={f.rules} onChange={(v) => set("rules", v)} />
          </>}
          {isHotel && <PairListEditor label="Policy sections" value={f.sections} aKey="heading" bKey="body" aPh="Payment policy" bPh="Policy text…" textarea onChange={(v) => set("sections", v)} />}
          {isDining && <PairListEditor label="Restrictions (optional)" value={f.restrictions} aKey="label" bKey="detail" aPh="Adults only" bPh="Adults-only venue." onChange={(v) => set("restrictions", v)} />}

          {isDining && !(f.type === "pool_menu_collection") && <HoursEditor value={f.schedule} onChange={(v) => set("schedule", v)} />}

          {/* Image */}
          <div>
            <Label>Image (optional)</Label>
            <div className="flex items-center gap-3 mt-1">
              {f.image_url
                ? <div className="relative"><img src={assetUrl(f.image_url)} alt="" className="h-16 w-16 rounded-lg object-cover" /><button onClick={removeImg} className="absolute -top-2 -right-2 bg-white rounded-full border p-0.5 shadow"><X className="h-3.5 w-3.5" /></button></div>
                : <div className="h-16 w-16 rounded-lg bg-[hsl(var(--muted))] grid place-items-center text-[hsl(var(--muted-foreground))]"><ImagePlus className="h-5 w-5" /></div>}
              <label className="inline-flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm font-medium cursor-pointer hover:bg-[hsl(var(--muted))]">
                <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload"}
                <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} data-testid="explore-image-input" />
              </label>
            </div>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Only upload verified hotel photos. Items display a clean icon layout when no image is set.</p>
          </div>

          <div><Label>Internal notes (not shown to attendees)</Label><Textarea rows={2} value={f.internal_notes || ""} onChange={(e) => set("internal_notes", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="explore-save" onClick={save} disabled={saving}>{saving ? "Saving…" : (isNew ? "Create" : "Save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinesEditor({ label, value, onChange }) {
  return (
    <div>
      <Label>{label} (one per line)</Label>
      <Textarea rows={4} value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />
    </div>
  );
}

function PairListEditor({ label, value, aKey, bKey, aPh, bPh, textarea, onChange }) {
  const rows = value || [];
  const upd = (i, k, v) => { const arr = rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)); onChange(arr); };
  const add = () => onChange([...rows, { [aKey]: "", [bKey]: "" }]);
  const del = (i) => onChange(rows.filter((_, j) => j !== i));
  return (
    <div>
      <div className="flex items-center justify-between"><Label>{label}</Label><Button type="button" size="sm" variant="outline" onClick={add}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button></div>
      <div className="space-y-2 mt-1">
        {rows.map((r, i) => (
          <div key={i} className="flex items-start gap-2">
            <div className="flex-1 space-y-1">
              <Input value={r[aKey] || ""} placeholder={aPh} onChange={(e) => upd(i, aKey, e.target.value)} />
              {textarea
                ? <Textarea rows={2} value={r[bKey] || ""} placeholder={bPh} onChange={(e) => upd(i, bKey, e.target.value)} />
                : <Input value={r[bKey] || ""} placeholder={bPh} onChange={(e) => upd(i, bKey, e.target.value)} />}
            </div>
            <button onClick={() => del(i)} className="text-[hsl(var(--muted-foreground))] hover:text-red-600 mt-2"><X className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function HoursEditor({ value, onChange }) {
  const days = value?.days || {};
  const setDay = (d, periods) => onChange({ days: { ...days, [d]: periods } });
  return (
    <div>
      <Label>Weekly hours</Label>
      <div className="space-y-2 mt-1">
        {DOW.map(([d, label]) => {
          const periods = days[d] || [];
          const is24 = periods.some((p) => p.is_24h);
          return (
            <div key={d} className="rounded-lg border border-[hsl(var(--border))] p-2.5" data-testid={`hours-day-${d}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm text-[hsl(var(--primary))]">{label}</span>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs"><Switch checked={is24} onCheckedChange={(v) => setDay(d, v ? [{ label: "Open 24 hours", open: "00:00", close: "00:00", is_24h: true }] : [])} /> 24h</label>
                  {!is24 && <Button type="button" size="sm" variant="outline" onClick={() => setDay(d, [...periods, { label: "", open: "", close: "", close_next_day: false }])}><Plus className="h-3 w-3" /></Button>}
                </div>
              </div>
              {!is24 && periods.length === 0 && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Closed</div>}
              {!is24 && periods.map((p, i) => (
                <div key={i} className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  <Input className="h-8 w-28" placeholder="Label" value={p.label || ""} onChange={(e) => setDay(d, periods.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                  <Input className="h-8 w-24" type="time" value={p.open || ""} onChange={(e) => setDay(d, periods.map((x, j) => j === i ? { ...x, open: e.target.value } : x))} />
                  <span className="text-xs">–</span>
                  <Input className="h-8 w-24" type="time" value={p.close || ""} onChange={(e) => setDay(d, periods.map((x, j) => j === i ? { ...x, close: e.target.value } : x))} />
                  <label className="flex items-center gap-1 text-[11px]"><input type="checkbox" checked={!!p.close_next_day} onChange={(e) => setDay(d, periods.map((x, j) => j === i ? { ...x, close_next_day: e.target.checked } : x))} /> +1d</label>
                  <button onClick={() => setDay(d, periods.filter((_, j) => j !== i))} className="text-[hsl(var(--muted-foreground))] hover:text-red-600"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
